import { Container, Flex, Card, Box, Heading } from '@radix-ui/themes';
import { useVariantValue } from '@trashlab/sitesapp/src/organizations/piedmont/lib/utils/get-variant-value';
import { Image } from '@trashlab/sitesapp/src/organizations/piedmont/components/shared/image';
import { useTenantContext } from '@trashlab/sitesapp/src/organizations/piedmont/contexts/use-tenant-context';

type DumpsterType = 'residential' | 'commercial' | 'rolloff' | 'portableToilet';

interface DumpsterMapRatesProps {
  type: DumpsterType;
}

export const DumpsterMapRates = ({ type }: DumpsterMapRatesProps) => {
  const { isEditMode } = useTenantContext();

  const mapRatesTitle = useVariantValue('DUMPSTER_MAP_RATES_TITLE', '');

  const residentialMapRatesImage = useVariantValue(
    'RESIDENTIAL_MAP_RATES_IMAGE',
    '',
  );
  const commercialMapRatesImage = useVariantValue(
    'COMMERCIAL_MAP_RATES_IMAGE',
    '',
  );
  const rolloffMapRatesImage = useVariantValue(
    'ROLLOFF_MAP_RATES_IMAGE',
    '',
  );
  const portableToiletMapRatesImage = useVariantValue(
    'PORTABLE_TOILET_MAP_RATES_IMAGE',
    '',
  );

  const getMapRatesImage = () => {
    switch (type) {
      case 'residential':
        return residentialMapRatesImage;
      case 'commercial':
        return commercialMapRatesImage;
      case 'rolloff':
        return rolloffMapRatesImage;
      case 'portableToilet':
        return portableToiletMapRatesImage;
    }
  };

  // Get the variable type for map rates images
  const getMapRatesImageVariable = (typeValue: DumpsterType) => {
    switch (typeValue) {
      case 'residential':
        return 'RESIDENTIAL_MAP_RATES_IMAGE';
      case 'commercial':
        return 'COMMERCIAL_MAP_RATES_IMAGE';
      case 'rolloff':
        return 'ROLLOFF_MAP_RATES_IMAGE';
      case 'portableToilet':
        return 'PORTABLE_TOILET_MAP_RATES_IMAGE';
      default:
        return '';
    }
  };

  const getServiceTypeLabel = () => {
    switch (type) {
      case 'residential':
        return 'Residential';
      case 'commercial':
        return 'Commercial';
      case 'rolloff':
        return 'Rolloff';
      case 'portableToilet':
        return 'Portable Toilet';
      default:
        return 'Service';
    }
  };

  const mapRatesImage = getMapRatesImage();

  // Only render if there's a map image OR if in edit mode (to show edit overlay)
  if (!mapRatesImage && !isEditMode) return null;

  return (
    <Container
      size="4"
      p="8"
      id="dumpster-map-rates"
      className={`relative min-h-[200px] overflow-hidden before:content-[''] before:absolute before:inset-0 before:bg-[url('/default/rental-steps-bg.svg')] before:bg-cover before:bg-center before:bg-no-repeat before:opacity-100 before:-z-10 after:content-[''] after:absolute after:inset-0 after:opacity-90 after:-z-10`}
    >
      <Flex direction="column" gap="4" align="center">
        <Card className="w-full border border-gray-200 shadow-sm">
          <Box p="6" mb="2" data-variable="DUMPSTER_MAP_RATES_TITLE">
            {mapRatesTitle && <Heading as="h2" size="8">{mapRatesTitle}</Heading>}
          </Box>

          <Flex
            direction="column"
            gap="4"
            align="center"
            data-variable={getMapRatesImageVariable(type)}
            className="min-h-[300px] w-full"
          >
            {mapRatesImage && (
              <Image
                src={mapRatesImage}
                alt={`${getServiceTypeLabel()} service area map and rates`}
                className="w-full max-w-full rounded-lg max-h-[600px] object-cover"
              />
            )}
          </Flex>
        </Card>
      </Flex>
    </Container>
  );
};

export default DumpsterMapRates;
